/**
 * Snapshot panel — renders a single invoice version snapshot with optional
 * diff highlighting against a previous version.
 *
 * Decomposed into section sub-components to satisfy lint rules.
 */

import { formatCurrency } from '@/utils/formatters'

import {
  str, formatSnapDate, getSupplyTypeLabel, formatAddress,
  isDiff, diffWrap, changedClass,
} from './invoice-version-types'

import type { RawInvoiceSnapshot, RawItemSnapshot } from './invoice-version-types'

// ── Shared helpers scoped to this file ──────────────────────────

type CurFn = (amount: unknown, currency?: string) => string

interface SectionProps {
  snap: RawInvoiceSnapshot
  prevSnap?: RawInvoiceSnapshot
  shouldHighlight: boolean
}

// ── Invoice Details Section ─────────────────────────────────────

function InvoiceDetailsSection({ snap, prevSnap, shouldHighlight }: SectionProps): JSX.Element {
  const dw = (c: unknown, p: unknown): string => diffWrap(shouldHighlight, c, p)
  const ch = (c: unknown, p: unknown): string => changedClass(shouldHighlight, c, p)

  return (
    <section>
      <p className="text-xs font-semibold text-foreground-secondary uppercase tracking-wider mb-2">Invoice Details</p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 border border-border rounded-lg el-p-sm">
        <div className={dw(snap.invoice_number, prevSnap?.invoice_number)}>
          <p className="text-xs text-foreground-secondary mb-0.5">Invoice #</p>
          <p className={`text-sm ${ch(snap.invoice_number, prevSnap?.invoice_number)}`}>{str(snap.invoice_number)}</p>
        </div>
        <div className={dw(snap.invoice_date, prevSnap?.invoice_date)}>
          <p className="text-xs text-foreground-secondary mb-0.5">Date</p>
          <p className={`text-sm ${ch(snap.invoice_date, prevSnap?.invoice_date)}`}>{formatSnapDate(snap.invoice_date)}</p>
        </div>
        <div className={dw(snap.due_date, prevSnap?.due_date)}>
          <p className="text-xs text-foreground-secondary mb-0.5">Due Date</p>
          <p className={`text-sm ${ch(snap.due_date, prevSnap?.due_date)}`}>{formatSnapDate(snap.due_date)}</p>
        </div>
        <div>
          <p className="text-xs text-foreground-secondary mb-0.5">Supply Type</p>
          <p className="text-sm text-foreground">{getSupplyTypeLabel(snap.supply_type)}</p>
        </div>
        {snap.place_of_supply && (
          <div className={dw(snap.place_of_supply, prevSnap?.place_of_supply)}>
            <p className="text-xs text-foreground-secondary mb-0.5">Place of Supply</p>
            <p className={`text-sm ${ch(snap.place_of_supply, prevSnap?.place_of_supply)}`}>{snap.place_of_supply}</p>
          </div>
        )}
        <div className={dw(snap.currency, prevSnap?.currency)}>
          <p className="text-xs text-foreground-secondary mb-0.5">Currency</p>
          <p className={`text-sm ${ch(snap.currency, prevSnap?.currency)}`}>{str(snap.currency)}</p>
        </div>
        {snap.po_number && (
          <div className={dw(snap.po_number, prevSnap?.po_number)}>
            <p className="text-xs text-foreground-secondary mb-0.5">PO #</p>
            <p className={`text-sm ${ch(snap.po_number, prevSnap?.po_number)}`}>{snap.po_number}</p>
          </div>
        )}
        {snap.payment_terms && (
          <div className={dw(snap.payment_terms, prevSnap?.payment_terms)}>
            <p className="text-xs text-foreground-secondary mb-0.5">Payment Terms</p>
            <p className={`text-sm ${ch(snap.payment_terms, prevSnap?.payment_terms)}`}>{snap.payment_terms}</p>
          </div>
        )}
      </div>
    </section>
  )
}

// ── Seller Section ──────────────────────────────────────────────

function SellerSection({ snap }: { snap: RawInvoiceSnapshot }): JSX.Element {
  const addrLine = formatAddress([snap.seller_address_line1, snap.seller_address_line2])
  const cityLine = formatAddress([snap.seller_city, snap.seller_state, snap.seller_postal_code])

  return (
    <section>
      <p className="text-xs font-semibold text-foreground-secondary uppercase tracking-wider mb-2">Seller</p>
      <div className="border border-border rounded-lg el-p-sm">
        <p className="text-sm font-semibold text-foreground">{str(snap.seller_name)}</p>
        {snap.seller_gstin && <p className="text-xs text-foreground-secondary">GSTIN: {snap.seller_gstin}</p>}
        {snap.seller_pan && <p className="text-xs text-foreground-secondary">PAN: {snap.seller_pan}</p>}
        {addrLine && <p className="text-xs text-foreground-secondary mt-1">{addrLine}</p>}
        {cityLine && <p className="text-xs text-foreground-secondary">{cityLine}</p>}
        {snap.seller_country && <p className="text-xs text-foreground-secondary">{snap.seller_country}</p>}
        {snap.seller_contact_email && <p className="text-xs text-foreground-secondary mt-1">{snap.seller_contact_email}</p>}
        {snap.seller_contact_phone && <p className="text-xs text-foreground-secondary">{snap.seller_contact_phone}</p>}
      </div>
    </section>
  )
}

// ── Bill-To Section ─────────────────────────────────────────────

function BillToCard({ snap, prevSnap, shouldHighlight }: SectionProps): JSX.Element {
  const ch = (c: unknown, p: unknown): string => changedClass(shouldHighlight, c, p)
  const partyAddrLine = formatAddress([snap.party_address_line1, snap.party_address_line2])
  const partyCityLine = formatAddress([snap.party_city, snap.party_state, snap.party_postal_code])

  return (
    <div>
      <p className="text-xs font-semibold text-foreground-secondary uppercase tracking-wider mb-2">Bill To</p>
      <div className="border border-border rounded-lg el-p-sm h-full">
        <p className={`text-sm font-semibold ${ch(snap.party_name, prevSnap?.party_name)}`}>{str(snap.party_name)}</p>
        {snap.party_gstin && (
          <p className={`text-xs ${ch(snap.party_gstin, prevSnap?.party_gstin)}`}>GSTIN: {snap.party_gstin}</p>
        )}
        {snap.party_company_vendor_code && (
          <p className="text-xs text-foreground-secondary">Vendor Code: {snap.party_company_vendor_code}</p>
        )}
        {partyAddrLine && (
          <p className={`text-xs mt-1 ${ch(snap.party_address_line1, prevSnap?.party_address_line1)}`}>{partyAddrLine}</p>
        )}
        {partyCityLine && (
          <p className={`text-xs ${ch(snap.party_city, prevSnap?.party_city)}`}>{partyCityLine}</p>
        )}
        {snap.party_country && <p className="text-xs text-foreground-secondary">{snap.party_country}</p>}
      </div>
    </div>
  )
}

// ── Ship-To Section ─────────────────────────────────────────────

function ShipToCard({ snap, prevSnap, shouldHighlight }: SectionProps): JSX.Element {
  const ch = (c: unknown, p: unknown): string => changedClass(shouldHighlight, c, p)
  const shipAddrLine = formatAddress([snap.ship_to_address_line1, snap.ship_to_address_line2])
  const shipCityLine = formatAddress([snap.ship_to_city, snap.ship_to_state, snap.ship_to_postal_code])

  return (
    <div>
      <p className="text-xs font-semibold text-foreground-secondary uppercase tracking-wider mb-2">Ship To</p>
      <div className="border border-border rounded-lg el-p-sm h-full">
        {snap.ship_to_gstin && (
          <p className={`text-xs ${ch(snap.ship_to_gstin, prevSnap?.ship_to_gstin)}`}>GSTIN: {snap.ship_to_gstin}</p>
        )}
        {shipAddrLine && (
          <p className={`text-xs mt-1 ${ch(snap.ship_to_address_line1, prevSnap?.ship_to_address_line1)}`}>{shipAddrLine}</p>
        )}
        {shipCityLine && (
          <p className={`text-xs ${ch(snap.ship_to_city, prevSnap?.ship_to_city)}`}>{shipCityLine}</p>
        )}
        {snap.ship_to_country && <p className="text-xs text-foreground-secondary">{snap.ship_to_country}</p>}
      </div>
    </div>
  )
}

// ── Bill-To / Ship-To Section ───────────────────────────────────

function BillShipSection({ snap, prevSnap, shouldHighlight }: SectionProps): JSX.Element {
  const hasShipTo = !!(snap.ship_to_address_line1 ?? snap.ship_to_city)

  return (
    <section className={`grid gap-3 ${hasShipTo ? 'grid-cols-2' : 'grid-cols-1'}`}>
      <BillToCard prevSnap={prevSnap} shouldHighlight={shouldHighlight} snap={snap} />
      {hasShipTo && <ShipToCard prevSnap={prevSnap} shouldHighlight={shouldHighlight} snap={snap} />}
    </section>
  )
}

// ── Line Items Table ────────────────────────────────────────────

interface LineItemsProps {
  snap: RawInvoiceSnapshot
  items: RawItemSnapshot[]
  prevItems: RawItemSnapshot[]
  shouldHighlight: boolean
  cur: CurFn
}

function rowBackgroundClass(isAdded: boolean, isChanged: boolean): string {
  if (isAdded) { return 'bg-green-50 dark:bg-green-950/20' }
  if (isChanged) { return 'bg-amber-50 dark:bg-amber-950/20' }
  return ''
}

function LineItemRow({ item, prevItem, isAdded, isChanged, shouldHighlight, isIntrastate, cur, currency }: {
  item: RawItemSnapshot
  prevItem?: RawItemSnapshot
  isAdded: boolean
  isChanged: boolean
  shouldHighlight: boolean
  isIntrastate: boolean
  cur: CurFn
  currency?: string
}): JSX.Element {
  const fc = (a: unknown, b: unknown): string =>
    shouldHighlight && isChanged && isDiff(a, b)
      ? 'text-amber-700 dark:text-amber-300' : ''

  return (
    <tr className={rowBackgroundClass(isAdded, isChanged)}>
      <td className="el-p-xs text-foreground-secondary">{item.line_number}</td>
      <td className="el-p-xs max-w-[160px]">
        <p className={`font-medium ${fc(item.description, prevItem?.description)}`}>{item.description ?? '—'}</p>
        {item.hsn_code && <p className="text-foreground-secondary">HSN: {item.hsn_code}</p>}
        {item.sac_code && <p className="text-foreground-secondary">SAC: {item.sac_code}</p>}
        {isAdded && <span className="text-xs text-green-700 dark:text-green-300 font-semibold">NEW</span>}
        {isChanged && <span className="text-xs text-amber-700 dark:text-amber-300 font-semibold">CHANGED</span>}
      </td>
      <td className={`el-p-xs text-right font-mono whitespace-nowrap ${fc(item.quantity, prevItem?.quantity)}`}>
        {item.quantity} {item.unit}
      </td>
      <td className={`el-p-xs text-right font-mono ${fc(item.unit_price, prevItem?.unit_price)}`}>
        {cur(item.unit_price, currency)}
      </td>
      <td className="el-p-xs text-right font-mono">{item.discount_percent ?? 0}%</td>
      <LineItemGstCells isIntrastate={isIntrastate} item={item} />
      <td className={`el-p-xs text-right font-mono font-medium ${fc(item.total_amount, prevItem?.total_amount)}`}>
        {cur(item.total_amount, currency)}
      </td>
    </tr>
  )
}

function LineItemGstCells({ item, isIntrastate }: {
  item: RawItemSnapshot
  isIntrastate: boolean
}): JSX.Element {
  if (isIntrastate) {
    return (
      <>
        <td className="el-p-xs text-right font-mono">{item.cgst_rate ?? 0}%</td>
        <td className="el-p-xs text-right font-mono">{item.sgst_rate ?? 0}%</td>
      </>
    )
  }
  return <td className="el-p-xs text-right font-mono">{item.igst_rate ?? 0}%</td>
}

function computeItemState(
  item: RawItemSnapshot,
  prevItems: RawItemSnapshot[],
  index: number,
  shouldHighlight: boolean
): { prevItem: RawItemSnapshot | undefined; isAdded: boolean; isChanged: boolean } {
  const prevItem = prevItems.find(p => p.line_number === item.line_number) ?? prevItems[index]
  const isAdded = shouldHighlight && !prevItems.find(p => p.line_number === item.line_number)
  const isChanged =
    shouldHighlight &&
    !isAdded &&
    !!prevItem &&
    (String(item.total_amount) !== String(prevItem.total_amount) ||
      item.description !== prevItem.description ||
      String(item.quantity) !== String(prevItem.quantity) ||
      String(item.unit_price) !== String(prevItem.unit_price))
  return { prevItem, isAdded, isChanged }
}

function LineItemsTableHead({ isIntrastate }: { isIntrastate: boolean }): JSX.Element {
  return (
    <thead>
      <tr className="bg-surface-secondary">
        <th className="text-left el-p-xs font-medium text-foreground-secondary">#</th>
        <th className="text-left el-p-xs font-medium text-foreground-secondary">Description</th>
        <th className="text-right el-p-xs font-medium text-foreground-secondary">Qty</th>
        <th className="text-right el-p-xs font-medium text-foreground-secondary">Rate</th>
        <th className="text-right el-p-xs font-medium text-foreground-secondary">Disc%</th>
        {isIntrastate ? (
          <>
            <th className="text-right el-p-xs font-medium text-foreground-secondary">CGST%</th>
            <th className="text-right el-p-xs font-medium text-foreground-secondary">SGST%</th>
          </>
        ) : (
          <th className="text-right el-p-xs font-medium text-foreground-secondary">IGST%</th>
        )}
        <th className="text-right el-p-xs font-medium text-foreground-secondary">Total</th>
      </tr>
    </thead>
  )
}

function RemovedItemRows({ prevItems, items, isIntrastate, snap, cur }: {
  prevItems: RawItemSnapshot[]
  items: RawItemSnapshot[]
  isIntrastate: boolean
  snap: RawInvoiceSnapshot
  cur: CurFn
}): JSX.Element {
  const removedItems = prevItems.filter(pi => !items.find(ii => ii.line_number === pi.line_number))

  return (
    <>
      {removedItems.map((item, i) => (
        <tr key={`removed-${i}`} className="bg-red-50 dark:bg-red-950/20">
          <td className="el-p-xs text-foreground-secondary">{item.line_number}</td>
          <td className="el-p-xs">
            <p className="font-medium line-through text-foreground-secondary">{item.description}</p>
            <span className="text-xs text-red-700 dark:text-red-300 font-semibold">REMOVED</span>
          </td>
          <td className="el-p-xs text-right font-mono line-through text-foreground-secondary">{item.quantity} {item.unit}</td>
          <td className="el-p-xs text-right font-mono line-through text-foreground-secondary">{cur(item.unit_price, snap.currency)}</td>
          <td className="el-p-xs text-right font-mono">{item.discount_percent ?? 0}%</td>
          {isIntrastate ? <><td className="el-p-xs" /><td className="el-p-xs" /></> : <td className="el-p-xs" />}
          <td className="el-p-xs text-right font-mono line-through text-foreground-secondary">{cur(item.total_amount, snap.currency)}</td>
        </tr>
      ))}
    </>
  )
}

function LineItemsSection({ snap, items, prevItems, shouldHighlight, cur }: LineItemsProps): JSX.Element | null {
  if (items.length === 0) { return null }

  const isIntrastate = !!snap.supply_type?.toLowerCase().includes('intrastate')

  return (
    <section className="border-t border-border pt-4">
      <p className="text-xs font-semibold text-foreground-secondary uppercase tracking-wider mb-2">Line Items</p>
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <LineItemsTableHead isIntrastate={isIntrastate} />
          <tbody className="divide-y divide-border">
            {items.map((item, i) => {
              const { prevItem, isAdded, isChanged } = computeItemState(item, prevItems, i, shouldHighlight)
              return (
                <LineItemRow
                  key={i}
                  cur={cur}
                  currency={snap.currency}
                  isAdded={isAdded}
                  isChanged={isChanged}
                  isIntrastate={isIntrastate}
                  item={item}
                  prevItem={prevItem}
                  shouldHighlight={shouldHighlight}
                />
              )
            })}
            {!shouldHighlight && (
              <RemovedItemRows
                cur={cur}
                isIntrastate={isIntrastate}
                items={items}
                prevItems={prevItems}
                snap={snap}
              />
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// ── Totals Section ──────────────────────────────────────────────

interface TotalRowDef {
  key: string
  label: string
  amount: unknown
  prevAmount: unknown
  visible: boolean
  prefix?: string
  highlight: boolean
}

function buildTotalRows(snap: RawInvoiceSnapshot, prevSnap?: RawInvoiceSnapshot): TotalRowDef[] {
  return [
    { key: 'subtotal',  label: 'Subtotal', amount: snap.subtotal, prevAmount: prevSnap?.subtotal, visible: Number(snap.subtotal) > 0, highlight: true },
    { key: 'discount',  label: 'Discount', amount: snap.discount_amount, prevAmount: prevSnap?.discount_amount, visible: Number(snap.discount_amount) > 0, prefix: '−', highlight: true },
    { key: 'taxable',   label: 'Taxable',  amount: snap.taxable_amount, prevAmount: prevSnap?.taxable_amount, visible: Number(snap.taxable_amount) > 0, highlight: false },
    { key: 'cgst',      label: 'CGST',     amount: snap.cgst_amount, prevAmount: prevSnap?.cgst_amount, visible: Number(snap.cgst_amount) > 0, highlight: true },
    { key: 'sgst',      label: 'SGST',     amount: snap.sgst_amount, prevAmount: prevSnap?.sgst_amount, visible: Number(snap.sgst_amount) > 0, highlight: true },
    { key: 'igst',      label: 'IGST',     amount: snap.igst_amount, prevAmount: prevSnap?.igst_amount, visible: Number(snap.igst_amount) > 0, highlight: true },
    { key: 'cess',      label: 'Cess',     amount: snap.cess_amount, prevAmount: prevSnap?.cess_amount, visible: Number(snap.cess_amount) > 0, highlight: false },
  ]
}

function TotalsTableRows({ snap, prevSnap, shouldHighlight, cur }: SectionProps & { cur: CurFn }): JSX.Element {
  const ch = (c: unknown, p: unknown): string => changedClass(shouldHighlight, c, p)
  const rows = buildTotalRows(snap, prevSnap)

  return (
    <>
      {rows.filter(r => r.visible).map((row) => (
        <tr key={row.key}>
          <td className="el-p-xs text-foreground-secondary">{row.label}</td>
          <td className={`el-p-xs text-right font-mono ${row.highlight ? ch(row.amount, row.prevAmount) : 'text-foreground'}`}>
            {row.prefix ?? ''}{cur(row.amount, snap.currency)}
          </td>
        </tr>
      ))}
      {!!snap.round_off_amount && (
        <tr>
          <td className="el-p-xs text-foreground-secondary">Round Off</td>
          <td className={`el-p-xs text-right font-mono ${ch(snap.round_off_amount, prevSnap?.round_off_amount)}`}>
            {Number(snap.round_off_amount) > 0 ? '+' : ''}
            {cur(snap.round_off_amount, snap.currency)}
          </td>
        </tr>
      )}
      <tr className="bg-surface-secondary">
        <td className="el-p-xs font-semibold text-foreground">Total</td>
        <td className={`el-p-xs text-right font-mono font-semibold ${ch(snap.total_amount, prevSnap?.total_amount)}`}>
          {cur(snap.total_amount, snap.currency)}
        </td>
      </tr>
    </>
  )
}

function TotalsSection({ snap, prevSnap, shouldHighlight, cur }: SectionProps & { cur: CurFn }): JSX.Element {
  return (
    <section>
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <tbody className="divide-y divide-border">
            <TotalsTableRows cur={cur} prevSnap={prevSnap} shouldHighlight={shouldHighlight} snap={snap} />
          </tbody>
        </table>
      </div>
    </section>
  )
}

// ── Bank Details Section ────────────────────────────────────────

function BankDetailField({ label, value }: { label: string; value?: string | null }): JSX.Element | null {
  if (!value) { return null }
  return (
    <div>
      <p className="text-xs text-foreground-secondary">{label}</p>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  )
}

const BANK_DETAIL_FIELDS: Array<{ key: keyof NonNullable<RawInvoiceSnapshot['bank_details']>; label: string }> = [
  { key: 'bank_name', label: 'Bank' },
  { key: 'account_name', label: 'Account Name' },
  { key: 'account_number', label: 'Account #' },
  { key: 'ifsc', label: 'IFSC' },
  { key: 'swift_code', label: 'SWIFT/BIC' },
  { key: 'iban', label: 'IBAN' },
  { key: 'routing_number', label: 'Routing #' },
  { key: 'sort_code', label: 'Sort Code' },
  { key: 'branch', label: 'Branch' },
]

function BankDetailsSection({ snap }: { snap: RawInvoiceSnapshot }): JSX.Element | null {
  if (!snap.bank_details) { return null }
  const details = snap.bank_details
  return (
    <section>
      <p className="text-xs font-semibold text-foreground-secondary uppercase tracking-wider mb-2">Bank Details</p>
      <div className="border border-border rounded-lg el-p-sm grid grid-cols-2 gap-x-4 gap-y-2">
        {BANK_DETAIL_FIELDS.map(({ key, label }) => (
          <BankDetailField key={key} label={label} value={details[key]} />
        ))}
      </div>
    </section>
  )
}

// ── Notes & Terms Section ───────────────────────────────────────

function NotesTermsSection({ snap, prevSnap, shouldHighlight }: SectionProps): JSX.Element | null {
  if (!snap.notes && !snap.terms_conditions) { return null }
  const ch = (c: unknown, p: unknown): string => changedClass(shouldHighlight, c, p)

  return (
    <section className="grid grid-cols-1 gap-3">
      {snap.notes && (
        <div>
          <p className="text-xs font-semibold text-foreground-secondary uppercase tracking-wider mb-1">Notes</p>
          <p className={`text-sm whitespace-pre-wrap ${ch(snap.notes, prevSnap?.notes)}`}>{snap.notes}</p>
        </div>
      )}
      {snap.terms_conditions && (
        <div>
          <p className="text-xs font-semibold text-foreground-secondary uppercase tracking-wider mb-1">Terms & Conditions</p>
          <p className={`text-sm whitespace-pre-wrap ${ch(snap.terms_conditions, prevSnap?.terms_conditions)}`}>{snap.terms_conditions}</p>
        </div>
      )}
    </section>
  )
}

// ── Main SnapshotPanel ──────────────────────────────────────────

interface SnapshotPanelProps {
  snap: RawInvoiceSnapshot
  items: RawItemSnapshot[]
  prevSnap?: RawInvoiceSnapshot
  prevItems?: RawItemSnapshot[]
  currency: string
}

export function SnapshotPanel({ snap, items, prevSnap, prevItems = [], currency }: SnapshotPanelProps): JSX.Element {
  const shouldHighlight = !!prevSnap
  const cur: CurFn = (amount, curr) => formatCurrency(Number(amount) || 0, curr ?? currency)

  return (
    <div className="flex flex-col gap-4">
      <InvoiceDetailsSection prevSnap={prevSnap} shouldHighlight={shouldHighlight} snap={snap} />
      <SellerSection snap={snap} />
      <BillShipSection prevSnap={prevSnap} shouldHighlight={shouldHighlight} snap={snap} />
      <LineItemsSection cur={cur} items={items} prevItems={prevItems} shouldHighlight={shouldHighlight} snap={snap} />
      <TotalsSection cur={cur} prevSnap={prevSnap} shouldHighlight={shouldHighlight} snap={snap} />
      <BankDetailsSection snap={snap} />
      <NotesTermsSection prevSnap={prevSnap} shouldHighlight={shouldHighlight} snap={snap} />
    </div>
  )
}
